import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {deleteClaims, generateInvoice, getClaims} from "../../action";
import "../../styles/theme.scss";
import {useTranslation} from "react-i18next";
import {Button, Chip, Grid, IconButton, Typography} from "@mui/material";
import {Link, useNavigate} from "react-router-dom";
import TableN from "../../reusableComponents/components/generics/TableN";
import {clearClaimResp} from "../../store/actions/exampleAction";
import DialogBox from "../../reusableComponents/components/dialogs/DialogBox";
import {LoadingButton} from "../../reusableComponents/components/generics/LoadingButton";
import CommonSnackbar from "../../reusableComponents/components/generics/CommonSnackbar";
import CardHeading from "../CardHeading";
import Card from "../Card";
import {decodeId, formatDate, formatNumber} from "../../utils/helper";
import {INVOICE_URL} from "../../utils/constant";
import {CLAIM_STATUS_CREATED, claimChipColor, claimStatusMapping} from "../../utils/statusMap";
import DeleteIcon from "@mui/icons-material/Delete";
import {client} from "../../store/store";
import { useConvention } from "../../hooks/useConvention";

function ClaimTable() {
  const fosaState = useSelector((state) => state.fosaReducer);
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = useSelector((state) => state.fosaReducer.ClaimTableData)
  const [policyHolderData, setPolicyHolderData] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageInfo, setPageInfo] = useState();
  const [endCursors, setEndCursors] = useState();

  const [startCursors, setStartCursors] = useState();

  let healthData = fosaState.pickerHealthFacilityInsurees[0];

  const [selection, setSelection] = useState([]);
  const [isSubmitConfirmVisible, setSubmitConfirmVisible] = useState(false);

  const [isSubmitting, setSubmitting] = useState(false);
  const [snackBarOption, setSnackbarOption] = useState({
    isOpen: false,
    message: undefined,
    severity: undefined,
  })

  const [deleteOption, setDeleteOption] = useState({
    isOpen: false,
    claim: null,
  })
  const [isDeleting, setDeleting] = useState(false)


  const closeSnackbar = useCallback(() => {
    setSnackbarOption({isOpen: false, message: undefined, severity: undefined})
  }, [setSnackbarOption])

  useEffect(() => {
    dispatch(clearClaimResp());
  }, [dispatch]);

  const loadData = useCallback(() => {
    let params = buildQueryParams({
      // filters: filter,
      filters: {},
      pageSize,
      beforeCursor: endCursors,
      afterCursor: startCursors,
      orderBy: ["-dateClaimed"],
      healthFacility_Location_Parent_Uuid: healthData?.location?.parent?.uuid,
      healthFacility_Location_Uuid: healthData?.location?.uuid,
      healthFacility_Uuid: healthData?.uuid,
    });
    if (
      healthData?.location?.parent?.uuid ||
      healthData?.location?.uuid ||
      healthData?.uuid
    ) {
      dispatch(getClaims(params));
    }
  }, [
    healthData,
    healthData?.location?.parent?.uuid,
    healthData?.location?.uuid,
    healthData?.uuid,
    page,
    pageSize,
  ]);

  const { isConventionnementActive } = useConvention();

  useEffect(() => {
    if (
      healthData?.location?.parent?.uuid &&
      healthData?.location?.uuid &&
      healthData?.uuid
    ) {
      loadData();
    }
  }, [
    healthData,
    page,
    pageSize,
    loadData
  ]);

  const buildQueryParams = (state) => {
    let params = [];

    if (state.filters) {
      params = Object.keys(state.filters)
        .filter((f) => !!state.filters[f]["filter"])
        .map((f) => state.filters[f]["filter"]);
    }

    if (!state.beforeCursor && !state.afterCursor) {
      params.push(`first: ${state.pageSize}`);
    }
    if (!!state.afterCursor) {
      params.push(`after: "${state.afterCursor}"`);
      params.push(`first: ${state.pageSize}`);
    }
    if (!!state.beforeCursor) {
      params.push(`before: "${state.beforeCursor}"`);
      params.push(`last: ${state.pageSize}`);
    }
    if (!!state.orderBy) {
      params.push(`orderBy: ["${state.orderBy}"]`);
    }
    if (!!state.healthFacility_Location_Parent_Uuid) {
      params.push(
        `healthFacility_Location_Parent_Uuid: "${state.healthFacility_Location_Parent_Uuid}"`
      );
    }
    if (!!state.healthFacility_Location_Uuid) {
      params.push(
        `healthFacility_Location_Uuid: "${state.healthFacility_Location_Uuid}"`
      );
    }
    if (!!state.healthFacility_Uuid) {
      params.push(`healthFacility_Uuid: "${state.healthFacility_Uuid}"`);
    }

    return params;
  };

  useEffect(() => {
    if (fosaState.ClaimTableData) {
      setPolicyHolderData(fosaState.ClaimTableData);
      setPageInfo(
        fosaState.ClaimTableDataPageInfo.totalCount
      );
      setStartCursors(
        fosaState.ClaimTableDataPageInfo?.endCursor
      );
      setEndCursors(
        fosaState.ClaimTableDataPageInfo?.startCursor
      );
    }
  }, [fosaState]);

  const handleDeleteClaim = useCallback(async () => {
    const {claim} = deleteOption
    setDeleteOption({
      isOpen: false,
      claim: null
    })

    setDeleting(true)

    try {
      const resp = await deleteClaims([claim.uuid])
      console.log('== resp', resp)
      setSnackbarOption({
        isOpen: true,
        message: `La feuille de soin numéro ${claim.code} à bien été supprimé!`,
        severity: 'success'
      })
      loadData()
    } catch (e) {
      console.error(e)
      setSnackbarOption({
        isOpen: true,
        message: 'Une erreur s\'est produite lors de la suppression de la feuille de soin!',
        severity: 'error'
      })
    } finally {
      setDeleting(false)
    }

  }, [deleteOption, setDeleteOption, loadData])

  const headers = () => {
    return [
      t("Claim No."),
      // t("Reference No."),
      t("Insuree Name"),
      t("DateClaimed"),
      t("Processed On"),
      t("ClaimedAmount"),
      t("ApprovedAmount"),
      t("Status"),
      "Paiement",
      t("Actions"),
    ];
  };

  const handleRowDoubleClick = (rowData) => {
    navigate(`/fosa/claimForm/${rowData.uuid}`);
  };

  const itemFormatters = () => {
    var row = [
      (claim) =>
        !!claim.code ? claim.code : "",

      (claim) =>
        !!claim.insuree
          ? claim.insuree?.otherNames
          : "",
      (claim) => {
        return formatDate(claim.dateClaimed, 'dd/MM/yyyy');
      },
      (claim) => {
        return formatDate(claim.dateProcessed, 'dd/MM/yyyy')
      },
      (claim) => formatNumber(claim.claimed),
      (claim) => formatNumber(claim.approved),

      (claim) => {
        const statusValue = claim.status;
        const isContentieux = statusValue == 1 && !claim?.isFullRejected
        
        const statusText = claimStatusMapping(t)[isContentieux ? 128 : statusValue]
        return <Chip label={statusText} color={claimChipColor(claim.status)} style={{textTransform: 'uppercase'}}/>;
      },
      (claim) => {
        if (!claim.invoice) {
          return null
        }
        const id = decodeId(claim.invoice.id);
        return <Link to={`${INVOICE_URL}/${id}`}>{claim.invoice?.invoiceNumber}</Link>;
      },
      (claim) => {
        return (
          <div style={{display: "flex", alignItems: "center", justifyContent: "end"}}>
            <Button
              onClick={() => handleRowDoubleClick(claim)}
              variant="text"
            >
              Voir
            </Button>
            {claim.status === CLAIM_STATUS_CREATED && !claim.invoice && isConventionnementActive && (
              <IconButton
                disabled={isDeleting}
                color="danger"
                onClick={() => setDeleteOption({
                  isOpen: true,
                  claim,
                })}>
                <DeleteIcon/>
              </IconButton>
            )}
          </div>
        );
      },
    ];

    return row;
  };

  const handleRowsPerPageChange = (newPageSize) => {
    setPageSize(newPageSize);
    setEndCursors(null);
    setStartCursors(null);
    setPage(0); // Reset to the first page when changing page size
  };

  const handleNavigation = (direction) => {
    if (direction === "forward") {
      setEndCursors(null);
    } else if (direction === "backward") {
      setStartCursors(null);
    }
  };

  const performClaimSubmit = useCallback(async (note) => {
    setSubmitConfirmVisible(false);

    setSubmitting(true)
    try {
      const response = await generateInvoice(selection, null, note)
      console.log('Invoice response', response)
      if (!response.success) {
        throw new Error(response.data)
      }

      setSnackbarOption({
        isOpen: true,
        message: 'La demande de paiement à bien été créer!',
        severity: 'success'
      })

      const invoiceId = decodeId(response.data.invoice.id);

      navigate(`${INVOICE_URL}/${invoiceId}`);


    } catch (e) {
      console.error(e)
      setSnackbarOption({
        isOpen: true,
        message: 'Une erreur s\'est produite, veuillez réessayer',
        severity: 'error'
      })
    } finally {
      setSubmitting(false)
    }
  }, [selection, setSnackbarOption, setSubmitting]);

  return (
    <Grid container>
      {/* <TableN/> */}
      <Grid item xs={12}>
        <Card>
          <CardHeading
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              <Typography variant="h6">{t("ClaimList")}</Typography>
            </Grid>
            <Grid item>
              {selection.length > 0 && (
                <LoadingButton
                  variant="outlined"
                  onClick={() => setSubmitConfirmVisible(true)}
                  loading={isSubmitting}
                >
                  Créer une demande de paiement
                </LoadingButton>
              )}
            </Grid>
          </CardHeading>

          <TableN
            headers={headers()}
            itemFormatters={itemFormatters()}
            items={policyHolderData ? policyHolderData : []}
            //withSelection={"single"}
            withPagination={true}
            withCheckbox={true}
            rowsPerPageOptions={[10, 50, 100]}
            defaultPageSize={5}
            page={page}
            pageSize={pageSize}
            count={pageInfo}
            onChangePage={(event, newPage) => {
              setPage(newPage);
              handleNavigation(newPage > page ? "forward" : "backward");
            }}
            onChangeRowsPerPage={handleRowsPerPageChange}
            onDoubleClick={handleRowDoubleClick}
            selectedItems={selection}
            onSelectedChanged={(selection) => {
              setSelection(selection);
              console.log("selection changed", selection);
            }}
            allowSelection={(item) => {
              return item.invoice === null && item.attachmentsCount > 0 && isConventionnementActive;
            }}
            // rowLocked={this.rowLocked}
          />
        </Card>
      </Grid>

      <DialogBox
        open={isSubmitConfirmVisible}
        onClose={() => setSubmitConfirmVisible(false)}
        onAgree={performClaimSubmit}
        title="Confirmation"
        content={t("Are you sure you want to Submit the Claim?")}
        agreeLabel={t("Submit")}
        disagreeLabel={t("cancel")}
        inputLabel="Note"
      />

      <DialogBox
        open={deleteOption.isOpen}
        onClose={() => setDeleteOption(({isOpen: false, claim: null}))}
        title="Confirmation"
        content={`Voulez-vous supprimer la feuille de soin numéro : ${deleteOption.claim?.code}`}
        agreeLabel="Supprimer"
        disagreeLabel="Annuler"
        onAgree={handleDeleteClaim}
      />

      <CommonSnackbar
        open={snackBarOption.isOpen}
        onClose={closeSnackbar}
        message={snackBarOption.message}
        severity={snackBarOption.severity}
      />
    </Grid>
  );
}

export default ClaimTable;
